import type { Sort } from "@/composables/marketplace";
import { ApiClient } from "@/services/apiClient";
import type { CollectibleId } from "@/services/collectibles";
import type { CategoryId } from "@/services/collection/categories";
import { MarketplaceItem_Schema } from "@/services/marketplace/item/marketplaceItemSchema";
import { MarketplaceItemNotFoundException, type MarketplaceItem, type MarketplaceItem_Api_CreationData, type MarketplaceItem_Api_UpdateData, type MarketplaceItemId } from "@/services/marketplace/item/marketplaceItemTypes";
import { handleAxiosError, WholeNumber_Schema } from "@/utilities";
import type { AxiosResponse } from "axios";
import { z } from "zod";

export const fetchMarketplaceItemById = async (id: MarketplaceItemId): Promise<MarketplaceItem> => {
  const url = "api-public/market-place/market-place-item/" + id;
  return ApiClient.getPublic<MarketplaceItem>(url)
    .then(({ data }) => MarketplaceItem_Schema.parse(data))
    .catch(
      handleAxiosError({
        404: () => {
          throw new MarketplaceItemNotFoundException();
        },
      })
    );
};

const ApiResponse_MarketplaceItemList_Schema = z.object({
  items: MarketplaceItem_Schema.array(),
  totalItemsCount: WholeNumber_Schema,
});
type ApiResponse_MarketplaceItemList = z.infer<typeof ApiResponse_MarketplaceItemList_Schema>;

export type FetchListFilter = Partial<{
  page: number;
  perPage: number;
  sort: Sort;
  categoryId: CategoryId[];
  collectible: CollectibleId;
}>;

/** @deprecated Use searchCollectionItemsElastic() instead. */
export const fetchMarketplaceItemList = async (filter: FetchListFilter): Promise<ApiResponse_MarketplaceItemList> => {
  const url = "api-public/market-place/market-place-item";
  return ApiClient.getPublic<ApiResponse_MarketplaceItemList>(url, filter).then(({ data }) => ApiResponse_MarketplaceItemList_Schema.parse(data));
};

export const createMarketplaceItem = async (data: MarketplaceItem_Api_CreationData): Promise<MarketplaceItem> => {
  const url = "api/market-place/market-place-item";
  return ApiClient.post<MarketplaceItem>(url, {}, data).then(({ data }) => MarketplaceItem_Schema.parse(data));
};

export const updateMarketplaceItem = async (id: MarketplaceItemId, data: MarketplaceItem_Api_UpdateData): Promise<MarketplaceItem> => {
  const url = "api/market-place/market-place-item/" + id;
  return ApiClient.put<MarketplaceItem>(url, {}, data).then(({ data }) => MarketplaceItem_Schema.parse(data));
};

export const deleteMarketplaceItem = async (id: MarketplaceItemId): Promise<AxiosResponse> => {
  const url = "api/market-place/market-place-item/" + id;
  return ApiClient.delete(url);
};

export const adminWithdrawMarketplaceItem = async (id: MarketplaceItemId): Promise<AxiosResponse> => {
  const url = "api/market-place/market-place-item-unpublish/" + id;
  return ApiClient.post(url);
};
